import { module } from 'modujs'
import { clamp, map } from '../utils/maths'
import gsap from 'gsap'
import BezierEasing from 'bezier-easing'

const INNER_RATIO =  159 / 470
const VELOCITY_FACTOR = 1.5

export default class extends module {
    constructor(m) {
        super(m)

        // Binding
        this.onResizeEndBind = this.onResizeEnd.bind(this)
        this.onUpdateBind    = this.onUpdate.bind(this)
        this.onScrollBind    = this.onScroll.bind(this)

        // UI
        this.$el     = this.el
        this.$inner  = this.$('inner')[0]
        this.$visual = this.$('visual')[0]
        this.$video = this.$('video')[0]
        this.$shapes = this.$('shape')

        // Data
        this.videoSrc = {
            desktop: this.$video.dataset.src,
            mobile:  this.$video.dataset.srcMobile
        }

        // Flags
        this.isDeviceLandscape = false
        this.isRafPlaying = false
        this.isInview = false

        // Scaling values
        this.scaling = {
            start: 0,
            end: 1,
            min: .6,
            max: 1,
            lerp: .2,
            progress: 0,
            smoothProgress: 0,
            value: 0,
            smoothValue: 0
        }

        // Translating values
        this.translating = {
            start: 0,
            end: 1,
            lerp: .2,
            value: 0,
            smoothValue: 0,
        }

        this.overlay = {
            start: 0,
            end: 1,
            max: .9,
            value: 0
        }

        // Ease in scaling animation
        this.easing = BezierEasing(0.550, 0.085, 0.680, 0.530)
    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        this.bindEvents()
        this.computeMetrics()
        
        // If 2 videos src
        if (this.videoSrc.mobile) this.computeVideoSrc()

        // Set minimum scale value
        this.$el.style.setProperty('--scaling-value', this.scaling.min)
    }

    destroy() {
        this.unbindEvents()
        super.destroy()
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener('resizeEnd', this.onResizeEndBind)
        document.addEventListener('scroll',  this.onScrollBind)
    }

    unbindEvents() {
        window.removeEventListener('resizeEnd', this.onResizeEndBind)
        document.removeEventListener('scroll',  this.onScrollBind)
    }

    ///////////////
    // Callbacks
    ///////////////
    onResizeEnd() {
        if (this.scaling.value === this.scaling.max) {
            // Compute metrics after comparing values
            this.computeMetrics()
        
            requestAnimationFrame(() => {
                this.scaling.value = this.scaling.max
                this.play()
            })
        } else {
            this.computeMetrics()
        }

        // If 2 videos src
        if (this.videoSrc.mobile) this.computeVideoSrc()
    }

    onInview(args) {
        this.isInview = args.way === 'enter'
    }

    onScroll() {
        if (this.isInview) this.play()
    }

    onScrollProgress(progress) {
        // Translating
        this.translating.value = progress < this.translating.end ? (window.scrollVelocity * VELOCITY_FACTOR) : 0

        // Scaling progress
        this.scaling.progress = clamp(
            0, 
            1, 
            map(progress, this.scaling.start, this.scaling.end, 0, 1)
        )

        this.scaling.smoothProgress = this.easing(this.scaling.progress)

        // Scaling value
        this.scaling.value = clamp(
            this.scaling.min, 
            this.scaling.max, 
            map(this.scaling.smoothProgress, 0, 1, this.scaling.min, this.scaling.max)
        )

        // Parallax value
        this.overlay.value = clamp(
            0, 
            this.overlay.max, 
            map(progress, this.overlay.start, this.overlay.end, 0, this.overlay.max)
        )
    }
    
    onUpdate() {
        // Shapes translating
        // ==============================================
        this.translating.smoothValue += (this.translating.value - this.translating.smoothValue) * this.translating.lerp
        this.translating.smoothValue = ((1000 * (this.translating.smoothValue + 0.001)) | 0) / 1000
        
        this.computeTranslate()

        // Mask scaling
        // ==============================================
        this.scaling.smoothValue += (this.scaling.value - this.scaling.smoothValue) * this.scaling.lerp
        this.scaling.smoothValue = ((1000 * (this.scaling.smoothValue + 0.001)) | 0) / 1000

        this.computeScale()

        // Parallax
        // ==============================================
        this.computeOverlay()

        // Add delay before stopping raf
        // ==============================================
        if (Math.round(this.translating.smoothValue) === Math.round(this.translating.value) 
         && Math.round(this.scaling.smoothValue) === Math.round(this.scaling.value)) {
            this.rafIncrement++
            if (this.rafIncrement >= 50) this.pause()
        }
    }

    ///////////////
    // Methods
    ///////////////
    computeVideoSrc() {
        let videoSrc

        if (window.matchMedia("(max-aspect-ratio: 1/1) and (max-width: 700px)").matches) {
            videoSrc = this.$video.dataset.srcMobile
        } else {
            videoSrc = this.$video.dataset.src
        }

        if (videoSrc !== this.$video.src) {
            const wasPlaying = !this.$video.paused
            this.$video.src = videoSrc

            if (wasPlaying) {
                this.$video.play()
            }
        }
    }

    computeMetrics() {
        // Global boolean
        this.isDeviceLandscape = window.innerWidth > window.innerHeight

        // Set progress ranges 
        // ==============================================
        this.translating.end = window.innerHeight / this.$el.clientHeight
        this.scaling.start = window.innerHeight / this.$el.clientHeight
        this.scaling.end = 1 - (window.innerHeight / this.$el.clientHeight)
        this.overlay.start = this.scaling.end

        // Calc scaling ratio
        // ==============================================

        // Get shape size
        const shapeSize = Math.min(this.$inner.clientHeight, this.$inner.clientWidth)

        // Get inner shape size
        const innerShapeSize = shapeSize * INNER_RATIO

        // Get hypothenuse of inner shape size
        const initialSize = Math.sqrt(Math.pow(innerShapeSize, 2) + Math.pow(innerShapeSize, 2))
        
        // Calc final size
        const halfViewportHeight = .5 * window.innerHeight
        const halfViewportWidth  = .5 * window.innerWidth

        const halfHeightHypothenuse = Math.sqrt(Math.pow(halfViewportHeight, 2) + Math.pow(halfViewportHeight, 2))
        const halfWidthHypothenuse = Math.sqrt(Math.pow(halfViewportWidth, 2) + Math.pow(halfViewportWidth, 2))
        const finalSize = halfHeightHypothenuse + halfWidthHypothenuse

        // Set ratio between finale size and initial size
        this.scaling.max = finalSize / initialSize
    }

    computeTranslate() {
        // Set inline styles
        let index = 0
        while (index < this.$shapes.length) {
            const translate = (index + 1) * this.translating.smoothValue
            this.$shapes[index].style.transform = `translate3d(0, ${translate}px, 0)`

            index++
        }
    }

    computeScale() {
        this.$el.style.setProperty('--scaling-value', this.scaling.smoothValue)
    }

    computeOverlay() {
        this.$el.style.setProperty('--overlay-opacity', this.overlay.value)
    }

    // RAF
    ///////////////
    play() {
        if (this.isRafPlaying) return

        // console.log('play');

        this.isRafPlaying = true
        this.rafIncrement = 0

        gsap.ticker.add(this.onUpdateBind)
    }

    pause() {
        if (!this.isRafPlaying) return

        // console.log('pause');

        this.isRafPlaying = false

        gsap.ticker.remove(this.onUpdateBind)
    }
}
